import React, { useCallback, useState } from 'react';
import './AudienceTarget.scss';
import PropTypes from 'prop-types';
import { startCase } from 'lodash';
import SubmitButton from '../../../../common/SubmitButton/SubmitButton';
import Menu from '../../../../common/Menu/Menu';
import AudienceTargetModal from '../Modals/AudienceTargetModal/AudienceTargetModal';
import Spinner from 'react/components/common/Spinner/Spinner';
import * as Mixpanel from '../../../../../infra/mixpanel/Mixpanel';
import { CHANNELS } from '../../../../../../data/audience-segment-builder-helper';
import { buildUnifiedTvActivationBody } from '../../../../../services/AudienceInsightsService';

const UNIFIED_TV_CHANNEL = 'unified_tv';

const AudienceTarget = ({
  marketsAndAdvertisersPromise,
  audienceId,
  audienceName,
  audienceSegment,
  isActivateEnabled,
  isActivateAudienceEnabled,
  isActivateAudienceVisible,
  isActivateTargetEnabled,
  activateAudienceDisabledText,
  channel,
  getSegmentIds,
  createAmplifiedAudience,
  createAlwaysOnAudience,
  refreshAlwaysOnAudience,
  notificator,
  updateAudienceDeterministicActivated,
  updateAudienceAmplifiedActivated,
  updateAudienceAlwaysOnActivated,
  dspService,
  isAudienceDeterministicActivated,
  isAudienceAmplifiedActivated,
  isAudienceAlwaysOnActivated,
  activatedAmplifiedThreshold,
  activatedAlwaysOnThreshold,
  activatedMarket,
  activatedAdvertiser,
  activatedDataContractId,
  activatedDataContractText,
  activatedCategoryId,
  ssoService,
  getAmplifiedEstimatedReachGoal,
  createDeterministicAudience,
  createUserListForDeterministicAudience,
  createUnifiedTvActivation,
  handleActivatingAudience,
  handleActivatingTarget,
  widgetName,
  hasAmplificationModeSelector,
  getDataContract,
  getTaxonomyCategory,
  getAmpModelCategory,
  isDeterministicDynamicAllowed,
  isContextualUniquenessError,
  allowDeterministicSegment,
  allowCustomPriceSegment,
  allowOptimizeSegment,
  selectedCategory,
  onActivateSelectedCategory,
  isCategoryTargetingVisible,
  onUnifiedAudiencesActivated,
}) => {
  const [isOpenAudienceTargetModal, setIsOpenAudienceTargetModal] = useState(false);
  const [isTargetModalProcessing, setIsTargetModalProcessing] = useState(false);
  const [isActivationMenuOpen, setIsActivationMenuOpen] = useState(false);
  // PHOB-1857: only an activation round-trip (handleActivatingAudience, which saves the audience and can
  // create/rename it) produces authoritative id/name, so prefer it once we have it. Until then fall back
  // to the LIVE props: an audience that is already activated skips that round-trip entirely, and this
  // component mounts before any audience is loaded — freezing the mount-time props here would send an
  // empty segment_id/segment_name to the activation service (500) for every already-activated audience.
  const [freshAudienceData, setFreshAudienceData] = useState(null);
  const audienceData = freshAudienceData || { audienceId, audienceName };
  const [showSpinner, setShowSpinner] = useState(false);
  // PHOB-1710: activation state captured fresh from the controller at activate-time
  // (handleActivatingAudience reads live $scope). Preferred over the react2angular activation props,
  // which are not reliably re-propagated to this component (so a clone would otherwise open the modal
  // showing its source's always-on activation as already-active).
  const [freshActivation, setFreshActivation] = useState(null);
  // Set the moment an async amplified activation is fired, so the in-progress badge shows and
  const beginingErrorMsg = "Something went wrong... We couldn't";
  const tryAgainMsg = 'Please try again later.';
  const successMsg = (segmentType, time) =>
    `<p>Your ${segmentType} segment was successfully activated and will be available for targeting within ${time}.</p><p>Visit the segments list table to see your segment.</p>`;
  const inProgressMsg = (segmentName) =>
    `Activation of segment ${segmentName} is in progress. This may take a few minutes.`;

  const audienceTargetModalCustomSubmitHandler = useCallback(
    (
      selectedMarket,
      selectedAdvertiser,
      isDeterministic,
      isAmplified,
      amplifiedReachGoal,
      isDynamic,
      tremorSyndicateAmplified,
      tremorSyndicateDeterministic,
      customPriceAmplified,
      customPriceDeterministic,
      optimizationType,
      expirationDate,
      isCustomPriceDeterministic,
      sources
    ) => {
      onTargetAudienceCustomSubmit(
        selectedMarket,
        selectedAdvertiser,
        isDeterministic,
        isAmplified,
        amplifiedReachGoal,
        isDynamic,
        tremorSyndicateAmplified,
        tremorSyndicateDeterministic,
        customPriceAmplified,
        customPriceDeterministic,
        optimizationType,
        expirationDate,
        isCustomPriceDeterministic,
        sources
      );
    },
    [isOpenAudienceTargetModal]
  );
  const audienceTargetModalAlwaysOnSubmitHandler = useCallback(
    (dataContractId, dataContractText, categoryId, amplifiedReachGoal, isRefresh) => {
      onTargetAudienceAlwaysOnSubmit(dataContractId, dataContractText, categoryId, amplifiedReachGoal, isRefresh);
    },
    [isOpenAudienceTargetModal]
  );

  const audienceTargetModalCancelHandler = useCallback(
    () => setIsOpenAudienceTargetModal(false),
    [isOpenAudienceTargetModal]
  );

  const onTargetAudienceCustomSubmit = useCallback(
    async (
      selectedMarket,
      selectedAdvertiser,
      isDeterministic,
      isAmplified,
      amplifiedReachGoal,
      isDynamic,
      tremorSyndicateAmplified,
      tremorSyndicateDeterministic,
      customPriceAmplified,
      customPriceDeterministic,
      optimizationType,
      expirationDate,
      isCustomPriceDeterministic,
      sources
    ) => {
      // PHOB-1857: unified TV activates through abi-mgmt (activate_unified), which splits the parent
      // into per-source children and dispatches them. Build the contract body, POST it, and pass the
      // response's per-source audiences straight to the New Audiences popup.
      if (channel === UNIFIED_TV_CHANNEL) {
        setIsTargetModalProcessing(true);
        try {
          const { accessToken } = await ssoService.getAccessToken();
          const body = buildUnifiedTvActivationBody(audienceSegment, {
            market: selectedMarket,
            advertiser: selectedAdvertiser,
            sources,
            amplifyThreshold: amplifiedReachGoal?.value,
            isDynamic,
            tremorSyndicateAmplified,
            tremorSyndicateDeterministic,
            cpmRateAmplified: customPriceAmplified || null,
            cpmRateDeterministic: customPriceDeterministic || null,
            isCustomPriceDeterministic,
            optimizationType,
            expirationDate,
          });
          const response = await createUnifiedTvActivation(audienceData.audienceId, body, accessToken);
          setIsOpenAudienceTargetModal(false);
          setIsTargetModalProcessing(false);
          onUnifiedAudiencesActivated && onUnifiedAudiencesActivated(response.audiences);
        } catch (error) {
          setIsTargetModalProcessing(false);
          // PHOB-1857: this call goes through AngularJS $http (mgmt-service-factory), NOT axios, so the
          // rejection shape is { status, data, config, ... } — there is no `.response` wrapper.
          const errorBody = error?.data;
          const isAlreadyActivated =
            error?.status === 400 &&
            typeof errorBody === 'string' &&
            errorBody.toLowerCase().includes('already activated');
          if (isAlreadyActivated) {
            // Not a failure — the segment is already split/dispatched (stale tab or double-click).
            // Surface informationally; the read-only guards drive the clone flow on the next load.
            notificator.success({ body: 'This audience is already activated.' });
            setIsOpenAudienceTargetModal(false);
          } else {
            notificator.error({
              body: `${beginingErrorMsg} activate the audience "${audienceData.audienceName}". ${tryAgainMsg}`,
            });
          }
        }
        return;
      }
      setIsTargetModalProcessing(true);
      const { accessToken } = await ssoService.getAccessToken();
      await Promise.all([
        isDeterministic &&
          targetUserListAudience(
            selectedMarket,
            selectedAdvertiser,
            accessToken,
            isDynamic,
            tremorSyndicateDeterministic,
            customPriceDeterministic,
            expirationDate,
            isCustomPriceDeterministic
          ),
        isAmplified &&
          amplifyCustomAudience(
            selectedMarket,
            selectedAdvertiser,
            amplifiedReachGoal,
            accessToken,
            tremorSyndicateAmplified,
            customPriceAmplified,
            optimizationType
          ),
      ]);
      setIsOpenAudienceTargetModal(false);
      setIsTargetModalProcessing(false);
    },
    [audienceData, audienceSegment, channel, notificator, onUnifiedAudiencesActivated, createUnifiedTvActivation]
  );

  const onTargetAudienceAlwaysOnSubmit = useCallback(
    async (dataContractId, dataContractText, categoryId, amplifiedReachGoal, isRefresh) => {
      setIsTargetModalProcessing(true);
      const { accessToken } = await ssoService.getAccessToken();
      await amplifyAlwaysOnAudience(
        dataContractId,
        dataContractText,
        categoryId,
        amplifiedReachGoal,
        accessToken,
        isRefresh
      );
      setIsOpenAudienceTargetModal(false);
      setIsTargetModalProcessing(false);
    },
    [audienceData, audienceSegment, notificator]
  );

  const amplifyCustomAudience = useCallback(
    async (
      selectedMarket,
      selectedAdvertiser,
      amplifiedReachGoal,
      ssoAccessToken,
      tremorSyndicateAmplified,
      customPriceAmplified,
      optimizationType
    ) => {
      try {
        // Activate asynchronously: the activation service returns immediately while the (slow)
        // amplified segment is created in the background, so the UI no longer hits the 120s
        // gateway timeout. We show an in-progress toast now; the audience-builder controller polls
        // the activation status and shows the success/failure toast on completion.
        await createAmplifiedAudience(
          audienceSegment,
          audienceData.audienceId,
          audienceData.audienceName,
          channel,
          selectedMarket,
          selectedAdvertiser,
          amplifiedReachGoal.value,
          ssoAccessToken,
          tremorSyndicateAmplified,
          customPriceAmplified,
          optimizationType,
          true
        );
        notificator.success({ body: inProgressMsg(audienceData.audienceName) });
        // Mark amplified-activated so the existing flow blocks re-activating amplified (deterministic
        // stays available) and so the controller starts polling this audience's activation status.
        updateAudienceAmplifiedActivated(selectedMarket, selectedAdvertiser, amplifiedReachGoal.value);
      } catch (error) {
        // If our backend reports the segment is already activated (Mongo has it — e.g. the frontend's
        // view was stale and the user clicked again), that's NOT a failure: the segment IS activated.
        // Surface it benignly and refresh our state so re-activation is no longer offered (PHOB-1710).
        const body = error?.response?.data;
        const isAlreadyActivated =
          error?.response?.status === 400 && typeof body === 'string' && body.toLowerCase().includes('already active');
        if (isAlreadyActivated) {
          notificator.success({ body: `This audience is already activated.` });
          updateAudienceAmplifiedActivated(selectedMarket, selectedAdvertiser, amplifiedReachGoal.value);
        } else {
          notificator.error({
            body: `${beginingErrorMsg} amplify the audience "${audienceData.audienceName}". ${tryAgainMsg}`,
          });
        }
      }
    },
    [audienceData, audienceSegment, channel, notificator, ssoService]
  );

  const amplifyAlwaysOnAudience = useCallback(
    async (dataContractId, dataContractText, categoryId, alwaysOnReachGoal, ssoAccessToken, isRefresh) => {
      try {
        // Async (parity with amplified): returns immediately; controller polls always_on status and
        // toasts on completion. In-progress toast now; success/failure toast comes from polling.
        // PHOB-1862: when the category already has an amplification model, dispatch to the refresh
        // endpoint instead of create — same payload, same async/polling/toast behaviour.
        const activate = isRefresh ? refreshAlwaysOnAudience : createAlwaysOnAudience;
        await activate(
          audienceSegment,
          audienceData.audienceId,
          audienceData.audienceName,
          channel,
          dataContractId,
          dataContractText,
          categoryId,
          alwaysOnReachGoal.value,
          ssoAccessToken,
          true
        );
        notificator.success({ body: inProgressMsg(audienceData.audienceName) });
        updateAudienceAlwaysOnActivated(dataContractId, dataContractText, categoryId, alwaysOnReachGoal.value);
      } catch (error) {
        // A 400 "already active" means it's already activated in our system — benign, not a failure.
        const body = error?.response?.data;
        const isAlreadyActivated =
          error?.response?.status === 400 && typeof body === 'string' && body.toLowerCase().includes('already active');
        if (isAlreadyActivated) {
          notificator.success({ body: `This audience is already activated.` });
          updateAudienceAlwaysOnActivated(dataContractId, dataContractText, categoryId, alwaysOnReachGoal.value);
        } else {
          notificator.error({
            body: `${beginingErrorMsg} amplify the audience "${audienceData.audienceName}". ${tryAgainMsg}`,
          });
        }
      }
    },
    [audienceData, audienceSegment, channel, notificator, ssoService, createAlwaysOnAudience, refreshAlwaysOnAudience]
  );

  const targetUserListAudience = useCallback(
    async (
      selectedMarket,
      selectedAdvertiser,
      ssoAccessToken,
      isDynamic,
      tremorSyndicateDeterministic,
      customPriceDeterministic,
      expirationDate,
      isCustomPriceDeterministic
    ) => {
      try {
        const createDeterministicRes = await createDeterministicAudience(
          audienceData.audienceId,
          audienceData.audienceName,
          channel,
          selectedMarket,
          selectedAdvertiser,
          ssoAccessToken,
          undefined,
          tremorSyndicateDeterministic,
          customPriceDeterministic,
          expirationDate
        );
        if (!createDeterministicRes) throw new Error('No response from createDeterministicAudience');
        const { categoryId, dataContractId, dataProviderId, dspSegmentId } = createDeterministicRes;
        notificator.success({ body: successMsg('deterministic', '24 hours') });
        createUserListForDeterministicAudience(
          channel,
          audienceSegment,
          audienceData.audienceId,
          selectedMarket,
          selectedAdvertiser,
          categoryId,
          dataContractId,
          dataProviderId,
          dspSegmentId,
          isDynamic,
          ssoAccessToken,
          audienceData.audienceName,
          tremorSyndicateDeterministic,
          isCustomPriceDeterministic
        );
        updateAudienceDeterministicActivated(selectedMarket, selectedAdvertiser);
      } catch (error) {
        notificator.error({
          body: `${beginingErrorMsg} create the audience "${audienceData.audienceName}" in DMP. ${tryAgainMsg}`,
        });
      }
    },
    [audienceData, audienceSegment, channel, notificator]
  );

  const mixpanelTrackAudienceTargetClick = (channel) => {
    // Mixpanel block. Dynamically get the app location from location hash for correct Mixpanel track info variety
    const hashString = location && location.hash ? location.hash.split('/')[1] : '';
    const appLocation = startCase(hashString).split(' ').join('-') || 'Audience';
    Mixpanel.track(`${appLocation} - Activate Audience Button Clicked`, {
      'Audience Name': audienceData.audienceName,
      appName: 'Audience',
      channel,
      widget: widgetName || hashString,
    });
  };

  const isAudienceActive = () =>
    channel === 'articles'
      ? isAudienceAmplifiedActivated
      : isAudienceDeterministicActivated || isAudienceAmplifiedActivated;

  const activateAudience = async () => {
    setShowSpinner(!!audienceName);
    let activationState = null;
    if (!isAudienceActive()) {
      const { canceled, audienceId, audienceName, activationState: fresh } = await handleActivatingAudience();
      if (canceled) {
        setShowSpinner(false);
        return;
      }
      setFreshAudienceData({ audienceId, audienceName });
      activationState = fresh || null;
    }
    // PHOB-1710: prefer the freshly-resolved activation over the (possibly stale) react2angular props.
    setFreshActivation(activationState);
    mixpanelTrackAudienceTargetClick(channel);
    setIsOpenAudienceTargetModal(true);
    setShowSpinner(false);
  };

  const activateTarget = async () => {
    setShowSpinner(!!audienceName);
    if (!isAudienceActive()) {
      const { canceled } = await handleActivatingAudience();
      if (canceled) {
        setShowSpinner(false);
        return;
      }
    }
    setShowSpinner(false);
    handleActivatingTarget();
  };

  const getAudienceTargetClickable = () => {
    const buttonClass = 'activate-button';
    const isDisabled = !isActivateEnabled || (!isActivateAudienceEnabled && !isActivateTargetEnabled);
    const commonProps = {
      className: buttonClass,
      label: 'Activate',
      isDisabled,
      onClick: () => setIsActivationMenuOpen(true),
    };

    if (channel === CHANNELS.hisense?.name && isDisabled) {
      commonProps.onDisabledTooltip = activateAudienceDisabledText;
    }

    return (
      <SubmitButton {...commonProps}>
        <Menu
          attachToParent={true}
          isMenuOpen={isActivationMenuOpen}
          closeMenu={() => setIsActivationMenuOpen(false)}
          className="activation-menu"
          menuActions={[
            {
              label: 'Audience Targeting',
              disabled: !isActivateAudienceEnabled,
              hide: !isActivateAudienceVisible,
              disabledText: activateAudienceDisabledText,
              onActionClick: () => {
                setIsActivationMenuOpen(false);
                activateAudience();
              },
            },
            {
              label: 'Contextual Targeting',
              disabled: !isActivateTargetEnabled || isContextualUniquenessError,
              disabledText: isContextualUniquenessError
                ? 'The audience you have selected is not unique enough. Please refine your Audience.'
                : null,
              onActionClick: () => {
                setIsActivationMenuOpen(false);
                activateTarget();
              },
            },
            {
              label: 'Selected Category Targeting',
              disabled: !selectedCategory,
              hide: !isCategoryTargetingVisible,
              disabledText: !selectedCategory ? 'Please select a category from the table' : null,
              onActionClick: () => {
                setIsActivationMenuOpen(false);
                if (onActivateSelectedCategory) {
                  onActivateSelectedCategory(selectedCategory);
                }
              },
            },
          ]}
        />
      </SubmitButton>
    );
  };

  // PHOB-1710: when we captured fresh activation at activate-time, use it; otherwise fall back to the
  // (react2angular) props. This is what keeps a freshly-cloned audience from opening the modal with a
  // stale "already activated" always-on state.
  const fa = freshActivation;
  const modalIsAudienceDeterministicActivated = fa
    ? fa.isAudienceDeterministicActivated
    : isAudienceDeterministicActivated;
  const modalIsAudienceAmplifiedActivated = fa ? fa.isAudienceAmplifiedActivated : isAudienceAmplifiedActivated;
  const modalIsAudienceAlwaysOnActivated = fa ? fa.isAudienceAlwaysOnActivated : isAudienceAlwaysOnActivated;
  const modalActivatedAmplifiedThreshold = fa ? fa.activatedAmplifiedThreshold : activatedAmplifiedThreshold;
  const modalActivatedAlwaysOnThreshold = fa ? fa.activatedAlwaysOnThreshold : activatedAlwaysOnThreshold;
  const modalActivatedMarket = fa ? fa.activatedMarket : activatedMarket;
  const modalActivatedAdvertiser = fa ? fa.activatedAdvertiser : activatedAdvertiser;
  const modalActivatedDataContractId = fa ? fa.activatedDataContractId : activatedDataContractId;
  const modalActivatedDataContractText = fa ? fa.activatedDataContractText : activatedDataContractText;
  const modalActivatedCategoryId = fa ? fa.activatedCategoryId : activatedCategoryId;

  return (
    <React.Fragment>
      <div className="audience-target-component">
        {getAudienceTargetClickable()}
        <div className="spinner-wrap" style={{ pointerEvents: showSpinner ? 'auto' : 'none' }}>
          <Spinner show={showSpinner} />
        </div>
      </div>

      {isOpenAudienceTargetModal && (
        <AudienceTargetModal
          isOpen={isOpenAudienceTargetModal}
          onSubmitCustom={audienceTargetModalCustomSubmitHandler}
          onSubmitAlwaysOn={audienceTargetModalAlwaysOnSubmitHandler}
          onCancel={audienceTargetModalCancelHandler}
          marketsAndAdvertisersPromise={marketsAndAdvertisersPromise}
          audienceName={audienceData.audienceName}
          isProcessing={isTargetModalProcessing}
          getSegmentIds={getSegmentIds}
          audienceSegment={audienceSegment}
          channel={channel}
          dspService={dspService}
          isAudienceDeterministicActivated={modalIsAudienceDeterministicActivated}
          isAudienceAmplifiedActivated={modalIsAudienceAmplifiedActivated}
          isAudienceAlwaysOnActivated={modalIsAudienceAlwaysOnActivated}
          activatedAmplifiedThreshold={modalActivatedAmplifiedThreshold}
          activatedAlwaysOnThreshold={modalActivatedAlwaysOnThreshold}
          activatedMarket={modalActivatedMarket}
          activatedAdvertiser={modalActivatedAdvertiser}
          activatedDataContractId={modalActivatedDataContractId}
          activatedDataContractText={modalActivatedDataContractText}
          activatedCategoryId={modalActivatedCategoryId}
          ssoService={ssoService}
          getAmplifiedEstimatedReachGoal={getAmplifiedEstimatedReachGoal}
          widgetName={widgetName}
          hasAmplificationModeSelector={hasAmplificationModeSelector}
          getDataContract={getDataContract}
          getTaxonomyCategory={getTaxonomyCategory}
          getAmpModelCategory={getAmpModelCategory}
          isDeterministicDynamicAllowed={isDeterministicDynamicAllowed}
          allowDeterministicSegment={allowDeterministicSegment}
          allowCustomPriceSegment={allowCustomPriceSegment}
          allowOptimizeSegment={allowOptimizeSegment}
        />
      )}
    </React.Fragment>
  );
};

AudienceTarget.propTypes = {
  marketsAndAdvertisersPromise: PropTypes.object,
  audienceId: PropTypes.string,
  audienceName: PropTypes.string,
  audienceSegment: PropTypes.array.isRequired,
  isActivateEnabled: PropTypes.bool,
  isActivateAudienceEnabled: PropTypes.bool,
  isActivateTargetEnabled: PropTypes.bool,
  isActivateAudienceVisible: PropTypes.bool,
  activateAudienceDisabledText: PropTypes.string,
  channel: PropTypes.string.isRequired,
  getSegmentIds: PropTypes.func.isRequired,
  createAmplifiedAudience: PropTypes.func.isRequired,
  createAlwaysOnAudience: PropTypes.func.isRequired,
  refreshAlwaysOnAudience: PropTypes.func.isRequired,
  createDeterministicAudience: PropTypes.func.isRequired,
  createUserListForDeterministicAudience: PropTypes.func.isRequired,
  createUnifiedTvActivation: PropTypes.func,
  notificator: PropTypes.object.isRequired,
  updateAudienceDeterministicActivated: PropTypes.func.isRequired,
  updateAudienceAmplifiedActivated: PropTypes.func.isRequired,
  updateAudienceAlwaysOnActivated: PropTypes.func.isRequired,
  dspService: PropTypes.object.isRequired,
  isAudienceDeterministicActivated: PropTypes.bool,
  isAudienceAmplifiedActivated: PropTypes.bool,
  isAudienceAlwaysOnActivated: PropTypes.bool,
  activatedAmplifiedThreshold: PropTypes.number,
  activatedAlwaysOnThreshold: PropTypes.number,
  activatedMarket: PropTypes.object,
  activatedAdvertiser: PropTypes.object,
  activatedDataContractId: PropTypes.number,
  activatedDataContractText: PropTypes.string,
  activatedCategoryId: PropTypes.number,
  ssoService: PropTypes.object.isRequired,
  getAmplifiedEstimatedReachGoal: PropTypes.func.isRequired,
  handleActivatingAudience: PropTypes.func.isRequired,
  handleActivatingTarget: PropTypes.func.isRequired,
  widgetName: PropTypes.string,
  hasAmplificationModeSelector: PropTypes.bool,
  getDataContract: PropTypes.func,
  getTaxonomyCategory: PropTypes.func,
  getAmpModelCategory: PropTypes.func,
  isDeterministicDynamicAllowed: PropTypes.bool,
  isContextualUniquenessError: PropTypes.bool,
  allowDeterministicSegment: PropTypes.bool,
  allowCustomPriceSegment: PropTypes.bool,
  allowOptimizeSegment: PropTypes.bool,
  selectedCategory: PropTypes.object,
  onActivateSelectedCategory: PropTypes.func,
  isCategoryTargetingVisible: PropTypes.bool,
  onUnifiedAudiencesActivated: PropTypes.func,
};

AudienceTarget.defaultProps = {
  isActivateEnabled: false,
  isActivateAudienceEnabled: false,
  isActivateTargetEnabled: false,
  selectedCategory: null,
  onActivateSelectedCategory: null,
  isActivateAudienceVisible: false,
  isCategoryTargetingVisible: false,
};

export default AudienceTarget;
